import { FC } from 'react';
import { useQuery } from 'react-query';
import {
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Box,
  Button,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { ReactComponent as Logo } from 'assets/logoDark.svg';
import { useStyles } from './styles';
import { SystemStatusResponse } from './types';
import server from 'server';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { ReactComponent as Logos } from 'assets/logos.svg';
const isOperational = (status?: string) =>
  ['ok', 'up', 'operational', 'live', 'healthy'].includes(
    (status || '').toLowerCase()
  );

const formatDate = (value?: unknown) => {
  if (typeof value !== 'string' || !value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SystemStatus: FC = () => {
  const classes = useStyles();
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<SystemStatusResponse>(
    'systemStatus',
    () => server.get('status').then((response) => response.data?.data ?? response.data),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
    }
  );

  const status = typeof data?.status === 'string' ? data.status : 'Unknown';
  const appName = typeof data?.app === 'string' ? data.app : 'EasyIdentity';
  const healthy = isOperational(status);
  useAppPreloader(isLoading);
  return (
    <div className={classes.page}>
      <div className={classes.topBar}>
        <Logo style={{ width: 140, height: 48 }} />
      </div>

      <main className={classes.content}>
        <div className={classes.headerRow}>
          <div>
            <Typography className={classes.pageTitle}>System Status</Typography>
            <Typography className={classes.subtitle}>
              Live status of EasyIdentity services. Auto-refreshes every 30 seconds.
            </Typography>
          </div>

          <Button
            className={classes.refreshButton}
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        <Divider style={{ marginBottom: 22, borderColor: '#e2e6ee' }} />

        {isError && !isLoading && (
          <Paper className={classes.statePaper} elevation={0}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography className={classes.errorText}>
              Unable to fetch status. Please try again.
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => refetch()}
              style={{ marginTop: 16, textTransform: 'none' }}
            >
              Try again
            </Button>
          </Paper>
        )}

        {data && !isLoading && (
          <>
            <Paper className={classes.overviewCard} elevation={0}>
              <div className={classes.overviewTop}>
                <div className={classes.identity}>
                  <div className={classes.identityIcon}>
                    <Logos width={32} height={32} />
                  </div>
                  <div>
                    <Typography className={classes.appName}>{appName}</Typography>
                    <Typography className={classes.appCaption}>
                      Identity Platform Overview
                    </Typography>
                  </div>
                </div>

                <Chip
                  icon={
                    healthy ? (
                      <CheckCircleIcon fontSize="small" />
                    ) : (
                      <ErrorIcon fontSize="small" />
                    )
                  }
                  label={status.toUpperCase()}
                  className={healthy ? classes.liveChip : classes.downChip}
                />
              </div>

              <div className={classes.details}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography className={classes.detailLabel}>Application</Typography>
                    <Typography className={classes.detailValue}>{appName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography className={classes.detailLabel}>Service Status</Typography>
                    <Typography className={classes.detailValue}>
                      {healthy ? 'Operational' : status}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography className={classes.detailLabel}>Service Since</Typography>
                    <Typography className={classes.detailValue}>
                      {formatDate(data.since)}
                    </Typography>
                  </Grid>
                </Grid>
              </div>
            </Paper>

            <Box mt={3}>
              <Typography className={classes.sectionHeading}>
                Status Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper className={classes.infoCard} elevation={0}>
                    <div className={classes.infoIcon}>
                      <AccessTimeIcon />
                    </div>
                    <div>
                      <Typography className={classes.infoLabel}>
                        Last Checked
                      </Typography>
                      <Typography className={classes.infoValue}>
                        {formatDate(data.checked_at)}
                      </Typography>
                    </div>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper className={classes.infoCard} elevation={0}>
                    <div className={classes.infoIcon}>
                      <RefreshIcon />
                    </div>
                    <div>
                      <Typography className={classes.infoLabel}>
                        Monitoring
                      </Typography>
                      <Typography className={classes.infoValue}>
                        Automatic refresh every 30 seconds
                      </Typography>
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <div className={classes.footer}>
              <span>EasyIdentity System Monitoring</span>
              <span>{isFetching ? 'Updating status...' : 'Updates automatically'}</span>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SystemStatus;

